/**
 * Logged-in landing page.
 *
 * Renders the brand hero (logo + "Internal Platform" eyebrow) above a
 * grid of section cards. Each card is a tile that navigates to one
 * area of the platform.
 *
 * Sales surfaces as a single card (Depletions). The Distribution card
 * was retired at HY's request — the invoiced-volume view isn't part of
 * how the team works, so it's no longer offered anywhere in the UI.
 *
 * The grid scales gracefully:
 *   1 card → centered single tile
 *   2 cards → side-by-side pair
 *   3 cards → row on desktop, single column on mobile
 *   4 cards → 2×2 on desktop
 *   5–6 cards → 3×2
 *   …more   → keeps flowing into rows of 3 maximum.
 *
 * Cards are role-aware: an entry only appears if the user can
 * actually access it. Admins see every section regardless (admin is a
 * wildcard via auth/RequireRole.tsx). This avoids the dead-end of
 * clicking into a section only to hit a Forbidden screen.
 */

import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import LocalBarOutlinedIcon from '@mui/icons-material/LocalBarOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import { Box, Container, Tooltip, Typography } from '@mui/material';
import { motion } from 'framer-motion';
import { Fragment, type ComponentType } from 'react';
import { useNavigate } from 'react-router-dom';

import { useQuery } from '@tanstack/react-query';

import { useEndRepViewOnHome } from '../api/actAs';
import { useUnreadNoteCount } from '../api/notes';
import { fetchLockedSections } from '../api/platformConfig';
import { useAuth } from '../auth/useAuth';
import { MomentumPanel } from '../components/MomentumPanel';
import { UserMenu } from '../components/UserMenu';
import { colors } from '../theme';

const MotionBox = motion.create(Box);

interface SectionCard {
  key: string;
  path: string;
  /** Role required to access this section. ``null`` means "no role
   *  gate" (everyone signed-in can enter). */
  requiredRole: string | null;
  /** Skip the admin-wildcard match.  Used by working-surface cards
   *  (e.g. the rep's Today's Plan / My Territories view) that would
   *  otherwise show admins an empty page they can't usefully act on
   *  — those admins should land on the oversight equivalent instead.
   *  Admins with an explicit grant for the role still see the card. */
  skipAdminWildcard?: boolean;
  Icon: ComponentType<{ sx?: object }>;
  /** Section identity colour — bg + border tinted from this hue when
   *  hovered, icon disc filled with a soft wash of it. Keeping each
   *  section a distinct tone so the catalog reads as a designed set
   *  rather than three identical cards. */
  tone: string;
  /** Display name (serif). */
  title: string;
  /** Uppercase gold eyebrow under the title. */
  eyebrow: string;
  /** One-line caption (body sans). */
  caption: string;
}

const SECTIONS: SectionCard[] = [
  {
    key: 'depletions',
    path: '/sales/depletions',
    requiredRole: 'depletions',
    Icon: LocalBarOutlinedIcon,
    tone: '#a86b1e',
    title: 'Depletions',
    eyebrow: 'Sales · Retail Pull-Through',
    caption: 'State, account, and product depletions trends with monthly comparisons.',
  },
  {
    key: 'marketing',
    path: '/marketing',
    requiredRole: 'marketing',
    Icon: CampaignOutlinedIcon,
    tone: '#22865a',
    title: 'Marketing',
    eyebrow: 'Intelligence & Insights',
    caption: 'Competitor watch, social patterns, and content recommendations.',
  },
  {
    key: 'field',
    path: '/regional-sales',
    requiredRole: 'field_rep',
    // Admins without an explicit field_rep grant skip this card — the
    // rep workspace is empty for them (no territory, no picks); the
    // oversight card below is the actionable entry point instead.
    skipAdminWildcard: true,
    Icon: MapOutlinedIcon,
    tone: '#8e4dab',
    title: 'Regional Sales',
    eyebrow: 'Sales · Follow-Up Tracker',
    caption: 'Today’s prioritized accounts, visit notes, and the activity log for your territory.',
  },
  {
    key: 'field-oversight',
    path: '/admin/regional-sales',
    requiredRole: 'admin',
    Icon: InsightsOutlinedIcon,
    tone: '#bb8c3f',
    title: 'Regional Sales Oversight',
    eyebrow: 'Admin · Field Activity',
    caption: 'Every rep, every territory — visit notes, activity timeline, and admin actions.',
  },
  {
    key: 'notes',
    path: '/notes',
    // No role gate: notes can be addressed to any active user, so
    // everyone needs a way in. Reps also reach this from a tab inside
    // their own workspace.
    requiredRole: null,
    Icon: ForumOutlinedIcon,
    tone: '#3f7d8c',
    title: 'Team Chat',
    eyebrow: 'Team · Direct & Group Messages',
    caption: 'Message a teammate or a group — asks, hand-offs, and follow-ups.',
  },
];

/** Convert a hex colour like #326eb8 to ``rgba(50, 110, 184, alpha)``.
 *  Used to derive soft tints without redefining the whole palette. */
function toRgba(hex: string, alpha: number): string {
  const v = hex.replace('#', '');
  const r = parseInt(v.slice(0, 2), 16);
  const g = parseInt(v.slice(2, 4), 16);
  const b = parseInt(v.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/** Art-deco style corner bracket — gold L-shape ornament. */
function CornerBracket({ position }: { position: 'tl' | 'tr' | 'bl' | 'br' }) {
  const top = position.startsWith('t');
  const left = position.endsWith('l');
  const size = { xs: 24, md: 32, xl: 40 };
  const inset = { xs: 24, md: 36, xl: 48 };

  return (
    <Box
      sx={{
        position: 'absolute',
        ...(top ? { top: inset } : { bottom: inset }),
        ...(left ? { left: inset } : { right: inset }),
        width: size,
        height: size,
        pointerEvents: 'none',
        zIndex: 1,
        '&::before, &::after': {
          content: '""',
          position: 'absolute',
          bgcolor: colors.gold,
          opacity: 0.55,
        },
        '&::before': {
          ...(top ? { top: 0 } : { bottom: 0 }),
          ...(left ? { left: 0 } : { right: 0 }),
          width: '100%',
          height: '1px',
        },
        '&::after': {
          ...(top ? { top: 0 } : { bottom: 0 }),
          ...(left ? { left: 0 } : { right: 0 }),
          height: '100%',
          width: '1px',
        },
      }}
    />
  );
}

export function LandingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const year = new Date().getFullYear();

  // DB-driven "Coming soon" list.  Cached for the session — this is a
  // tiny payload (a handful of strings) and the value only flips when
  // an admin explicitly toggles a section live, so re-fetching on focus
  // would just be churn.
  const { data: lockedData } = useQuery({
    queryKey: ['platform', 'locked-sections'],
    queryFn: fetchLockedSections,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
  const lockedSet = new Set(lockedData?.locked ?? []);

  // Filter cards by what this user can actually open OR is waiting
  // to open.  Admin is a wildcard so admins see every section
  // regardless of explicit grants — EXCEPT cards flagged
  // `skipAdminWildcard`, which require an explicit role grant.
  // That's how the rep workspace stays hidden from non-rep admins
  // (it would just render empty for them) while the matching
  // oversight card surfaces in its place.
  //
  // Locked sections still appear in the catalog — but ONLY for
  // users who have the matching role grant (or admins via the
  // wildcard).  A locked card visible to a user who'll never open
  // it ("Marketing" to a depletions-only rep) reads as broken
  // platform chrome.  We'd rather hide it cleanly than tease them
  // with a section they have no path into.
  // Unread count for the Team Chat card badge. Polled, so a note that
  // arrives while someone sits on this page still surfaces.
  const unreadNotes = useUnreadNoteCount().data?.unread_count ?? 0;

  // Reaching your own home page ends any rep view. Without this an
  // admin who viewed a rep and navigated away — rather than clicking
  // "Exit rep view" — would silently resume acting as them the next
  // time they opened Regional Sales.
  useEndRepViewOnHome();

  const isAdmin = !!user?.roles.some((r) => r.name === 'admin');
  // Only for people who actually work accounts. A depletions analyst
  // or a marketing user has no field activity to have momentum in, and
  // a ring reading 0/30 at someone who will never log a visit is a
  // permanently dead widget on the busiest page in the app.
  const isRep = !!user?.roles.some((r) => r.name === 'field_rep');
  const cards = SECTIONS.filter((s) => {
    if (!s.requiredRole) return true;
    const hasExplicitGrant = user?.roles.some((r) => r.name === s.requiredRole) ?? false;
    if (s.skipAdminWildcard) return hasExplicitGrant;
    if (isAdmin) return true;
    return hasExplicitGrant;
  });

  // Tightest grid that still reads as designed for each count.  We
  // deliberately skip layouts that would leave an orphan card on the
  // last row — a single half-width card alone reads as broken rather
  // than designed.
  //
  //   1 card  → single centered tile
  //   2 cards → side-by-side
  //   3 cards → 3-in-a-row at md+; below md we stack to 1 column
  //             (skipping the 2-col tablet layout that would orphan
  //             the third card)
  //   4 cards → 2×2 at sm/md, single row at lg+
  //   5 cards → stays 3-across at md+ with an accepted 3+2 last row;
  //             5+ is rare (only admins who are also field reps)
  //   6 cards → 3×2, fits cleanly
  //
  // gridMaxWidth caps the cards container so cards don't stretch to
  // absurd widths when there are few of them on a wide screen.
  const gridMaxWidth: Partial<Record<'xs' | 'sm' | 'md' | 'lg' | 'xl', number>> = (() => {
    if (cards.length <= 1) return { xs: 480, md: 460 };
    if (cards.length === 2) return { xs: 480, sm: 760, md: 920 };
    if (cards.length === 3) return { xs: 480, sm: 520, md: 1080, lg: 1200 };
    if (cards.length === 4) return { xs: 480, sm: 760, md: 920, lg: 1440 };
    return { xs: 480, md: 1280 }; // 5+ cards keep prior behaviour
  })();

  // minmax(0, 1fr), never a bare 1fr. `1fr` is shorthand for
  // minmax(auto, 1fr), and that `auto` floor is a grid item's
  // min-content width — so the longest unbreakable title on the page
  // ("Regional Sales Oversight") widened its own column and left the
  // tracks visibly unequal. A zero floor lets every column stay the
  // same width and the text wrap instead.
  const COL = 'minmax(0, 1fr)';
  const cols = (n: number) => `repeat(${n}, ${COL})`;

  const gridColumns =
    cards.length <= 1
      ? { xs: COL }
      : cards.length === 2
        ? { xs: COL, sm: cols(2) }
        : cards.length === 3
          ? // Skip the 2-col tablet layout (which orphans the 3rd
            // card); stay stacked through sm, then jump to 3-across
            // when md (≥900px) can comfortably fit them.
            { xs: COL, md: cols(3) }
          : cards.length === 4
            ? { xs: COL, sm: cols(2), lg: cols(4) }
            : { xs: COL, sm: cols(2), md: cols(3) };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: colors.brandCream,
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        // Anchor the composition near the top of the viewport rather
        // than vertically centering — when the cards don't fill the
        // screen, centering leaves a dead band of whitespace above
        // the hero.  Industry standard for landing heroes is a
        // deliberate top inset (roughly 6–10% of viewport).
        justifyContent: 'flex-start',
        pt: { xs: 5, md: 6, xl: 7 },
        pb: { xs: 5, md: 6, xl: 7 },
      }}
    >
      {/* Ambient warm wash centered on the focal point */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background:
            'radial-gradient(ellipse at 50% 50%, rgba(195,151,72,0.08) 0%, transparent 65%)',
          pointerEvents: 'none',
        }}
      />

      {/* Right-side brand hero image — fades from right edge into the
          cream.  Tuned to read with presence (clearly visible
          subject + tonal contrast) rather than as a faint backdrop. */}
      <MotionBox
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1.4, ease: 'easeOut' }}
        sx={{
          position: 'absolute',
          top: 0,
          right: 0,
          bottom: 0,
          width: { xs: 0, md: '46%', lg: '48%', xl: '50%' },
          backgroundImage: 'url(/brand/hero.jpg)',
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center right',
          backgroundSize: 'cover',
          // Higher opacity + brighter pass so the dagger / soldier
          // silhouette + American flag are unmistakable, but not so
          // hot that the Marketing card in front of it loses contrast.
          opacity: 0.78,
          maskImage:
            'linear-gradient(to right, transparent 0%, rgba(0,0,0,0.18) 22%, rgba(0,0,0,0.62) 50%, rgba(0,0,0,0.95) 78%, black 100%)',
          WebkitMaskImage:
            'linear-gradient(to right, transparent 0%, rgba(0,0,0,0.18) 22%, rgba(0,0,0,0.62) 50%, rgba(0,0,0,0.95) 78%, black 100%)',
          pointerEvents: 'none',
          // Less desaturation, slight brightness boost, and a touch of
          // contrast so the image reads punchier without going neon.
          filter: 'saturate(1.05) brightness(1.08) contrast(1.04)',
        }}
      />

      {/* Soft warm-gold breathing wash on top of the hero — kept
          subtle so it warms the scene without dimming it. */}
      <MotionBox
        animate={{ opacity: [0.35, 0.55, 0.35] }}
        transition={{ duration: 9, repeat: Infinity, ease: 'easeInOut' }}
        sx={{
          position: 'absolute',
          top: 0,
          right: 0,
          bottom: 0,
          width: { xs: 0, md: '58%' },
          backgroundImage:
            'radial-gradient(ellipse 60% 55% at 78% 50%, rgba(195,151,72,0.10) 0%, transparent 65%)',
          pointerEvents: 'none',
          mixBlendMode: 'screen',
        }}
      />

      {/* Top + bottom edge gold strips */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '2px',
          background: `linear-gradient(90deg, transparent, ${colors.gold}, transparent)`,
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: '2px',
          background: `linear-gradient(90deg, transparent, ${colors.gold}, transparent)`,
        }}
      />

      {/* Corner brackets — gold L-shape ornaments */}
      <CornerBracket position="tl" />
      <CornerBracket position="tr" />
      <CornerBracket position="bl" />
      <CornerBracket position="br" />

      {/* User-menu chip — floats in the top-right just inside the
          corner bracket so admin access (and sign-out) is reachable
          without first drilling into a section. */}
      <Box
        sx={{
          position: 'absolute',
          top: { xs: 16, md: 28, xl: 36 },
          right: { xs: 64, md: 80, xl: 96 },
          zIndex: 3,
        }}
      >
        <UserMenu />
      </Box>

      <Container
        maxWidth={false}
        sx={{
          position: 'relative',
          zIndex: 2,
          maxWidth: { xs: 720, md: 1280, xl: 1560 },
          px: { xs: 3, sm: 4, md: 6 },
        }}
      >
        {/* Hero — logo + eyebrow + bridge */}
        <MotionBox
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
          sx={{ textAlign: 'center', mb: { xs: 3.5, md: 4.5, xl: 5.5 } }}
        >
          <Box
            component="img"
            src="/brand/hy-logo.png"
            alt="Hooten Young"
            sx={{
              height: { xs: 100, md: 130, xl: 160 },
              width: 'auto',
              mb: { xs: 3, xl: 4 },
              display: 'block',
              mx: 'auto',
            }}
          />

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: { xs: 2.5, xl: 3 },
              maxWidth: { xs: 360, xl: 460 },
              mx: 'auto',
            }}
          >
            <Box
              sx={{
                flex: 1,
                height: '1px',
                background: `linear-gradient(90deg, transparent, ${colors.gold})`,
              }}
            />
            <Typography
              sx={{
                fontFamily: '"Avenir", "Inter", sans-serif',
                fontSize: { xs: 10, xl: 11.5 },
                fontWeight: 700,
                color: colors.gold,
                textTransform: 'uppercase',
                letterSpacing: '0.36em',
                whiteSpace: 'nowrap',
              }}
            >
              Internal Platform
            </Typography>
            <Box
              sx={{
                flex: 1,
                height: '1px',
                background: `linear-gradient(90deg, ${colors.gold}, transparent)`,
              }}
            />
          </Box>
        </MotionBox>

        {/* Momentum — above the section cards, so a rep sees where
            they stand before they choose where to go. Sized to the
            card grid so it reads as part of the same column rather
            than a banner bolted on top. */}
        {/* Cards */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: gridColumns,
            gap: { xs: 2.5, md: 3, xl: 4 },
            maxWidth: gridMaxWidth,
            mx: 'auto',
          }}
        >
          {cards.map((card, i) => (
            <SectionTile
              key={card.key}
              card={card}
              index={i}
              locked={lockedSet.has(card.key)}
              badgeCount={card.key === 'notes' ? unreadNotes : 0}
              onClick={() => navigate(card.path)}
            />
          ))}
        </Box>

        {/* Momentum sits below the cards, above the footer. It is a
            look-back, not a destination — putting it above the grid
            pushed the actual navigation down the page and made the
            first thing you see a report rather than a way in. */}
        {(isRep || isAdmin) && (
          <MotionBox
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.5 }}
            sx={{ maxWidth: gridMaxWidth, mx: 'auto', mt: { xs: 4, md: 5 } }}
          >
            {/* Your own window and nobody else's — the home page is a
                personal surface, so other people's activity belongs on
                the oversight page rather than everyone's front door.
                Admins get it too; the panel measures what each person
                can actually do, so an admin without a rep role sees
                their days on the platform rather than a permanent
                zero for visits they can't log. */}
            <MomentumPanel />
          </MotionBox>
        )}

        {/* Closing seal */}
        <MotionBox
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.7 }}
          sx={{ textAlign: 'center', mt: { xs: 4, md: 5, xl: 6 } }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2,
              maxWidth: { xs: 320, xl: 400 },
              mx: 'auto',
              mb: 1.75,
            }}
          >
            <Box
              sx={{
                flex: 1,
                height: '1px',
                background: `linear-gradient(90deg, transparent, ${colors.gold}55)`,
              }}
            />
            <Box
              sx={{
                width: 4,
                height: 4,
                borderRadius: '50%',
                bgcolor: colors.gold,
                opacity: 0.75,
              }}
            />
            <Box
              sx={{
                flex: 1,
                height: '1px',
                background: `linear-gradient(90deg, ${colors.gold}55, transparent)`,
              }}
            />
          </Box>
          <Typography
            sx={{
              fontFamily: '"Avenir", "Inter", sans-serif',
              fontSize: { xs: 10, xl: 11 },
              fontWeight: 600,
              color: colors.brandDark,
              letterSpacing: '0.45em',
              textTransform: 'uppercase',
              opacity: 0.7,
            }}
          >
            Made to Remember<sup style={{ fontSize: '0.6em', marginLeft: 1 }}>®</sup>
          </Typography>

          <Typography
            sx={{
              fontFamily: '"Avenir", "Inter", sans-serif',
              fontSize: { xs: 9.5, xl: 10 },
              fontWeight: 500,
              color: colors.textMuted,
              letterSpacing: '0.12em',
              mt: 1.25,
            }}
          >
            &copy; {year} Hooten Young American Whiskey · All rights reserved
          </Typography>

          {/* Utility links.  The User Guide sits beside What's New
              because both answer "help me understand this product" —
              one for people who are new, one for people catching up. */}
          <Box
            sx={{
              mt: 1.25,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1.5,
            }}
          >
            {(
              [
                { label: 'User Guide', to: '/user-guide' },
                { label: "What's New", to: '/whats-new' },
              ] as const
            ).map((link, i) => (
              <Fragment key={link.to}>
                {i > 0 && (
                  <Box
                    aria-hidden
                    sx={{ width: '1px', height: 10, bgcolor: colors.gold, opacity: 0.4 }}
                  />
                )}
                <Typography
                  onClick={() => navigate(link.to)}
                  sx={{
                    fontFamily: '"Avenir", "Inter", sans-serif',
                    fontSize: { xs: 9.5, xl: 10 },
                    fontWeight: 700,
                    color: colors.gold,
                    letterSpacing: '0.18em',
                    textTransform: 'uppercase',
                    cursor: 'pointer',
                    transition: 'opacity 0.15s',
                    opacity: 0.85,
                    '&:hover': { opacity: 1, textDecoration: 'underline' },
                  }}
                >
                  {link.label}
                </Typography>
              </Fragment>
            ))}
          </Box>
        </MotionBox>
      </Container>
    </Box>
  );
}

interface SectionTileProps {
  card: SectionCard;
  index: number;
  locked: boolean;
  onClick: () => void;
  /** Unread count shown as a gold pill in the card's corner. 0 (the
   *  default) renders nothing. Only Team Chat uses this today. */
  badgeCount?: number;
}

/** A single section card on the landing page.
 *
 *  Each tile shares the same chrome (cream paper, gold accent strip,
 *  corner detail, "Enter" affordance) but is differentiated by a
 *  section-identity colour (icon disc tint + a faint hover wash).
 *  The shared chrome lets the catalog read as one designed system
 *  while still giving each section a recognisable identity.
 *
 *  Locked variant: at rest the card looks live — full content,
 *  full colour — with only a tiny gold lock glyph in the upper-right
 *  corner as a quiet signal.  On hover the content fades far back
 *  and a centred "COMING SOON" plate fades in, flanked by the same
 *  hairline gold rules used on the hero's "INTERNAL PLATFORM"
 *  eyebrow.  That avoids the plate ever visually colliding with
 *  the title beneath it.  Click + keyboard activation are disabled. */
function SectionTile({ card, index, locked, onClick, badgeCount = 0 }: SectionTileProps) {
  const { Icon } = card;
  const toneSofter = toRgba(card.tone, 0.05);
  const toneMid = toRgba(card.tone, 0.32);

  const tile = (
    <Box
      onClick={locked ? undefined : onClick}
      role={locked ? undefined : 'button'}
      tabIndex={locked ? -1 : 0}
      aria-disabled={locked || undefined}
      onKeyDown={
        locked
          ? undefined
          : (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick();
              }
            }
      }
      sx={{
        position: 'relative',
        backgroundColor: 'rgba(253, 251, 245, 0.62)',
        backdropFilter: 'blur(12px) saturate(1.05)',
        WebkitBackdropFilter: 'blur(12px) saturate(1.05)',
        border: '1px solid rgba(255, 255, 255, 0.55)',
        borderRadius: 1,
        cursor: locked ? 'default' : 'pointer',
        // Fill the grid slot in BOTH axes. `height: 100%` was here from
        // the start so cards in a row share a height — but nothing
        // pinned the width, so each card sized to its own content
        // inside the track. "Regional Sales Oversight" is the longest
        // title on the page, so its card came out wider than the one
        // above it in the same column, which is the misalignment.
        //
        // width + flex + minWidth together: width alone is ignored once
        // this is a flex item, flex alone leaves min-content as a
        // floor, and minWidth: 0 removes that floor so the title wraps
        // instead of widening the card.
        height: '100%',
        width: '100%',
        flex: 1,
        minWidth: 0,
        px: { xs: 3, md: 3.25, xl: 3.75 },
        py: { xs: 3.5, md: 4, xl: 4.5 },
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        overflow: 'hidden',
        transition: 'all 0.45s cubic-bezier(0.4, 0, 0.2, 1)',
        boxShadow: '0 1px 2px rgba(45, 45, 44, 0.02), 0 8px 22px rgba(45, 45, 44, 0.05)',
        // Hairline top accent — paper-thin at rest, brighter on hover.
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '1px',
          background: `linear-gradient(to right, transparent, ${colors.gold}, transparent)`,
          opacity: 0.55,
          transition: 'opacity 0.4s ease',
        },
        // Hover state — locked tiles trade their normal content for
        // a centred "COMING SOON" plate so the lock state is only
        // surfaced on intent (and never overlaps the title at rest).
        '&:hover': locked
          ? {
              backgroundColor: 'rgba(253, 251, 245, 0.78)',
              '& .lock-veil': { opacity: 1 },
              '& .content-stack': { opacity: 0.08 },
              '& .lock-hint': { opacity: 0 },
            }
          : {
              backgroundColor: 'rgba(253, 251, 245, 0.78)',
              borderColor: toneMid,
              boxShadow: `0 2px 4px rgba(45, 45, 44, 0.04), 0 22px 50px ${toRgba(card.tone, 0.18)}`,
              transform: 'translateY(-4px)',
              '&::before': { opacity: 1 },
              '& .section-tone-wash': { opacity: 1 },
              '& .icon-mark': {
                color: card.tone,
                transform: 'translateY(-1px)',
              },
              '& .title-rule': {
                width: { xs: 36, md: 44, xl: 52 },
                opacity: 1,
                backgroundColor: card.tone,
              },
            },
        '&:focus-visible': locked
          ? { outline: 'none' }
          : {
              outline: 'none',
              borderColor: card.tone,
              boxShadow: `0 0 0 3px ${toRgba(card.tone, 0.22)}`,
            },
      }}
    >
      {/* Soft section-tone wash that comes in on hover. */}
      <Box
        className="section-tone-wash"
        aria-hidden
        sx={{
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: 0,
          height: '70%',
          background: `radial-gradient(ellipse 110% 90% at 50% 100%, ${toneSofter}, transparent 75%)`,
          opacity: 0,
          transition: 'opacity 450ms ease',
          pointerEvents: 'none',
        }}
      />

      {/* Resting "is locked" hint — small gold lock glyph on a cream
          chip in the upper-right corner.  The chip keeps the icon
          legible even when the card's translucent background sits
          over the dark hero image (the Marketing tile).  Fades out
          on hover when the full plate takes over. */}
      {locked && (
        <Box
          className="lock-hint"
          aria-hidden
          sx={{
            position: 'absolute',
            top: { xs: 10, md: 12 },
            right: { xs: 10, md: 12 },
            width: { xs: 22, md: 24 },
            height: { xs: 22, md: 24 },
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '50%',
            backgroundColor: 'rgba(253, 251, 245, 0.95)',
            border: `1px solid ${toRgba(colors.gold, 0.55)}`,
            boxShadow: '0 1px 3px rgba(45, 45, 44, 0.12)',
            color: colors.gold,
            transition: 'opacity 0.35s ease',
            zIndex: 1,
            '& svg': { fontSize: { xs: 13, md: 14 } },
          }}
        >
          <LockOutlinedIcon fontSize="inherit" />
        </Box>
      )}

      {/* Unread pill — same corner slot as the lock hint (the two are
          mutually exclusive: a locked section can't accrue unread
          items). Solid gold rather than the lock's outlined treatment,
          because this is a call to action, not a restriction. */}
      {!locked && badgeCount > 0 && (
        <Box
          aria-label={`${badgeCount} unread`}
          sx={{
            position: 'absolute',
            top: { xs: 10, md: 12 },
            right: { xs: 10, md: 12 },
            minWidth: { xs: 22, md: 24 },
            height: { xs: 22, md: 24 },
            px: 0.75,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '999px',
            backgroundColor: colors.gold,
            color: '#fff',
            fontFamily: '"Inter", sans-serif',
            fontSize: { xs: 11, md: 11.5 },
            fontWeight: 700,
            boxShadow: '0 1px 3px rgba(45, 45, 44, 0.18)',
            zIndex: 1,
          }}
        >
          {badgeCount > 99 ? '99+' : badgeCount}
        </Box>
      )}

      {/* Single centered content block — icon, title, rule, eyebrow,
            caption stacked tightly with no awkward space between
            zones.  This is the "luxury menu card" pattern: dense
            composition, generous edges, no filler. */}
      <Box
        className="content-stack"
        sx={{
          position: 'relative',
          width: '100%',
          transition: 'opacity 0.4s ease',
        }}
      >
        <Box
          className="icon-mark"
          sx={{
            color: 'rgba(15, 10, 7, 0.32)',
            mb: { xs: 1.5, xl: 1.75 },
            transition: 'color 0.4s ease, transform 0.4s ease',
            '& svg': {
              fontSize: { xs: 24, md: 26, xl: 30 },
            },
          }}
        >
          <Icon />
        </Box>

        <Typography
          sx={{
            // Same font stack as the eyebrow + body below — keeps
            // each card a single typographic system (Mac users see
            // Avenir throughout, others see Inter throughout).
            fontFamily: '"Avenir", "Inter", sans-serif',
            fontSize: { xs: 22, md: 24, xl: 28 },
            fontWeight: 700,
            color: colors.brandDark,
            letterSpacing: '-0.015em',
            lineHeight: 1.15,
            mb: { xs: 1, xl: 1.25 },
          }}
        >
          {card.title}
        </Typography>

        {/* Title rule — narrow at rest, widens + tints on hover. */}
        <Box
          className="title-rule"
          sx={{
            width: { xs: 18, md: 20, xl: 24 },
            height: '1px',
            backgroundColor: colors.gold,
            opacity: 0.7,
            mx: 'auto',
            mb: { xs: 1.25, xl: 1.5 },
            transition: 'width 0.45s ease, opacity 0.4s ease, background-color 0.4s ease',
          }}
        />

        <Typography
          sx={{
            fontFamily: '"Avenir", "Inter", sans-serif',
            fontSize: { xs: 9.5, xl: 10.5 },
            fontWeight: 700,
            color: colors.gold,
            textTransform: 'uppercase',
            letterSpacing: '0.26em',
            mb: { xs: 1.5, xl: 1.75 },
          }}
        >
          {card.eyebrow}
        </Typography>

        <Typography
          sx={{
            fontFamily: '"Avenir", "Inter", sans-serif',
            fontSize: { xs: 12, xl: 13 },
            color: 'rgba(15, 10, 7, 0.6)',
            lineHeight: 1.55,
            maxWidth: { xs: 260, xl: 300 },
            mx: 'auto',
          }}
        >
          {card.caption}
        </Typography>
      </Box>

      {/* Centred "COMING SOON" plate — hidden at rest, fades in on
          hover (driven by the parent's `&:hover .lock-veil` rule).
          Mirrors the hero's "INTERNAL PLATFORM" eyebrow treatment
          — gold hairline rules flanking gold-caps text — so the
          locked state reads as part of the brand's own visual
          language, not as a generic "disabled" overlay. */}
      {locked && (
        <Box
          className="lock-veil"
          aria-hidden
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'none',
            zIndex: 2,
            opacity: 0,
            transition: 'opacity 0.4s ease',
            // Near-opaque ivory wash so the plate stays legible even
            // on tiles that sit over the dark hero image (Marketing).
            // Tapers at the edges so the card's gold accent strip and
            // borders aren't completely hidden.
            background:
              'radial-gradient(ellipse 95% 88% at 50% 50%, rgba(250, 246, 238, 0.97) 0%, rgba(250, 246, 238, 0.9) 60%, rgba(250, 246, 238, 0.7) 85%, rgba(250, 246, 238, 0.4) 100%)',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: { xs: 1.5, md: 2 },
              maxWidth: '85%',
            }}
          >
            <Box
              sx={{
                flex: 1,
                height: '1px',
                minWidth: { xs: 24, md: 32 },
                background: `linear-gradient(90deg, transparent, ${colors.gold})`,
              }}
            />
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.75,
                color: colors.gold,
                fontFamily: '"Avenir", "Inter", sans-serif',
                fontSize: { xs: 10, md: 11 },
                fontWeight: 700,
                letterSpacing: '0.36em',
                textTransform: 'uppercase',
                whiteSpace: 'nowrap',
                '& svg': { fontSize: { xs: 12, md: 13 } },
              }}
            >
              <LockOutlinedIcon fontSize="inherit" />
              Coming Soon
            </Box>
            <Box
              sx={{
                flex: 1,
                height: '1px',
                minWidth: { xs: 24, md: 32 },
                background: `linear-gradient(90deg, ${colors.gold}, transparent)`,
              }}
            />
          </Box>
        </Box>
      )}
    </Box>
  );

  return (
    <MotionBox
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.55, delay: 0.28 + index * 0.1, ease: 'easeOut' }}
      // minWidth: 0 is the item-side half of the minmax(0, 1fr) on the
      // track. Without it a grid item still refuses to shrink below its
      // min-content width, and the column stretches anyway.
      sx={{ height: '100%', width: '100%', display: 'flex', minWidth: 0 }}
    >
      {locked ? (
        <Tooltip
          title={`${card.title} is launching soon — not yet released to the platform.`}
          arrow
          placement="top"
        >
          {/* Tooltip needs a non-disabled DOM child to attach to; the
              inner span carries the listener so the tile itself can
              stay aria-disabled.  Stretch the span so cards line up
              at the same height even when captions wrap differently. */}
          <span style={{ display: 'flex', flex: 1, width: '100%' }}>{tile}</span>
        </Tooltip>
      ) : (
        tile
      )}
    </MotionBox>
  );
}
